import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, Phone, Tag, ChevronLeft, ChevronRight, Users as UsersIcon } from 'lucide-react';
import { apiFetchPaginated } from '../lib/api';
import { formatRelative } from '../lib/format';

interface Customer {
  id: number;
  external_id: string | null;
  source_platform: 'api' | 'email' | 'whatsapp' | 'telegram' | 'widget';
  email: string | null;
  phone: string | null;
  name: string;
  created_at: string;
}

const PLATFORM_LABEL: Record<Customer['source_platform'], string> = {
  api: 'API',
  email: 'Correo',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  widget: 'Widget',
};

const PLATFORM_STYLE: Record<Customer['source_platform'], string> = {
  api: 'text-slate-700 bg-slate-100',
  email: 'text-blue-700 bg-blue-50',
  whatsapp: 'text-emerald-700 bg-emerald-50',
  telegram: 'text-sky-700 bg-sky-50',
  widget: 'text-indigo-700 bg-indigo-50',
};

export default function Customers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 20, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('per_page', '20');
    if (search.trim()) params.set('q', search.trim());

    apiFetchPaginated<Customer>(`/customers?${params.toString()}`)
      .then((res) => {
        setCustomers(res.data);
        setMeta(res.meta);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, page]);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Clientes</h1>
          <p className="text-sm text-slate-500 mt-1">
            {meta.total > 0
              ? `${meta.total} cliente${meta.total === 1 ? '' : 's'} registrado${meta.total === 1 ? '' : 's'}`
              : 'Aún no hay clientes registrados'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="p-4 border-b border-slate-100">
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por nombre, correo o teléfono…"
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white"
          />
        </div>

        {loading && (
          <div className="px-5 py-16 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Cargando clientes…
          </div>
        )}
        {error && !loading && (
          <div className="px-5 py-10 text-sm text-rose-600 text-center">{error}</div>
        )}
        {!loading && !error && customers.length === 0 && (
          <div className="px-5 py-16 text-sm text-slate-500 text-center">
            <UsersIcon className="mx-auto mb-2 text-slate-300" size={32} />
            No hay clientes que coincidan con la búsqueda.
          </div>
        )}

        {!loading && customers.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-medium">Nombre</th>
                  <th className="px-5 py-3 font-medium">Contacto</th>
                  <th className="px-5 py-3 font-medium">Origen</th>
                  <th className="px-5 py-3 font-medium">Registrado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {customers.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 transition">
                    <td className="px-5 py-3">
                      <Link
                        to={`/admin/customers/${c.id}`}
                        className="font-medium text-slate-900 hover:text-blue-600"
                      >
                        {c.name}
                      </Link>
                      {c.external_id && (
                        <div className="text-[11px] text-slate-500 mt-0.5 font-mono truncate max-w-[280px]">
                          {c.external_id}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-700 text-xs">
                      {c.email && (
                        <div className="flex items-center gap-1.5">
                          <Mail size={12} className="text-slate-400" /> {c.email}
                        </div>
                      )}
                      {c.phone && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Phone size={12} className="text-slate-400" /> {c.phone}
                        </div>
                      )}
                      {!c.email && !c.phone && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${PLATFORM_STYLE[c.source_platform]}`}
                      >
                        <Tag size={10} />
                        {PLATFORM_LABEL[c.source_platform]}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">
                      {formatRelative(c.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta.last_page > 1 && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
            <div className="text-slate-500">
              Página {meta.page} de {meta.last_page}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                disabled={page === meta.last_page}
                className="p-2 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
